// src/pages/admin/MasterData.tsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Search,
  Tag, BookOpen, Target, Calendar,
  Loader2, Check, X, Users, Microscope, Heart, Shield
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select, Textarea } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { KategoriMasalah, StandarSPMI, IndikatorSPMI, Periode } from '../../types';

type TabId = 'kategori' | 'standar' | 'indikator' | 'periode';

// Helper function to convert icon name string to React component
const getIconComponent = (iconName?: string | null): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    'BookOpen': <BookOpen className="w-6 h-6" />,
    'Users': <Users className="w-6 h-6" />,
    'Microscope': <Microscope className="w-6 h-6" />,
    'Heart': <Heart className="w-6 h-6" />,
    'Shield': <Shield className="w-6 h-6" />,
    'Tag': <Tag className="w-6 h-6" />,
  };
  
  return iconMap[iconName || ''] || <Tag className="w-6 h-6" />;
};

const MasterData: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabId>('kategori');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<TabId>('kategori');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data States
  const [kategori, setKategori] = useState<KategoriMasalah[]>([]);
  const [standar, setStandar] = useState<StandarSPMI[]>([]);
  const [indikator, setIndikator] = useState<IndikatorSPMI[]>([]);
  const [periode, setPeriode] = useState<Periode[]>([]);

  const [editItem, setEditItem] = useState<any>(null);

  // Form states
  const [kategoriForm, setKategoriForm] = useState({ nama: '', deskripsi: '', color: '#002060', icon: '' });
  const [standarForm, setStandarForm] = useState({ kode: '', nama: '', deskripsi: '' });
  const [indikatorForm, setIndikatorForm] = useState({ standar_spmi_id: '', kode: '', nama: '', deskripsi: '', target_value: '', satuan: '' }); // Note: satuan field might not be in IndikatorSPMI type yet, relying on custom payload or expanding type if needed. Type check: IndikatorSPMI has no 'satuan'. We will treat it as part of target_value string or ignore it if not supported.
  const [periodeForm, setPeriodeForm] = useState({ nama: '', tahun_ajaran: '', semester: '', tanggal_mulai: '', tanggal_selesai: '' });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') as TabId | null;
    if (tabParam && ['kategori', 'standar', 'indikator', 'periode'].includes(tabParam)) {
      setActiveTab(tabParam);
      return;
    }
    if (location.pathname.startsWith('/admin/standar')) {
      setActiveTab('standar');
    }
  }, [location.pathname, location.search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [katRes, stdRes, indRes, perRes] = await Promise.all([
        adminService.kategori.getAll(),
        adminService.standar.getAll(),
        adminService.indikator.getAll(),
        adminService.periode.getAll(),
      ]);

      setKategori(Array.isArray(katRes) ? katRes : (katRes as any).data || []);
      setStandar(Array.isArray(stdRes) ? stdRes : (stdRes as any).data || []);
      setIndikator(Array.isArray(indRes) ? indRes : (indRes as any).data || []);
      setPeriode(Array.isArray(perRes) ? perRes : (perRes as any).data || []);

    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat master data');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (type: TabId) => {
    setEditItem(null);
    setModalType(type);
    setKategoriForm({ nama: '', deskripsi: '', color: '#002060', icon: '' });
    setStandarForm({ kode: '', nama: '', deskripsi: '' });
    setIndikatorForm({ standar_spmi_id: '', kode: '', nama: '', deskripsi: '', target_value: '', satuan: '' });
    setPeriodeForm({ nama: '', tahun_ajaran: '', semester: '', tanggal_mulai: '', tanggal_selesai: '' });
    setShowModal(true);
  };

  const openEdit = (type: TabId, item: any) => {
    setEditItem(item);
    setModalType(type);
    if (type === 'kategori') setKategoriForm({ nama: item.nama, deskripsi: item.deskripsi || '', color: item.color, icon: item.icon || '' });
    if (type === 'standar') setStandarForm({ kode: item.kode, nama: item.nama, deskripsi: item.deskripsi || '' });
    if (type === 'indikator') setIndikatorForm({
      standar_spmi_id: item.standar_spmi_id.toString(),
      kode: item.kode,
      nama: item.nama,
      deskripsi: item.deskripsi || '',
      target_value: item.target_value || '',
      satuan: '' // Not stored separate field
    });
    if (type === 'periode') setPeriodeForm({
      nama: item.nama,
      tahun_ajaran: item.tahun_ajaran,
      semester: item.semester,
      tanggal_mulai: item.tanggal_mulai,
      tanggal_selesai: item.tanggal_selesai
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modalType === 'kategori') {
        const payload = { ...kategoriForm };
        if (editItem) await adminService.kategori.update(editItem.id, payload);
        else await adminService.kategori.create(payload);
      } else if (modalType === 'standar') {
        const payload = { ...standarForm };
        if (editItem) await adminService.standar.update(editItem.id, payload);
        else await adminService.standar.create(payload);
      } else if (modalType === 'indikator') {
        const payload = {
          ...indikatorForm,
          standar_spmi_id: Number(indikatorForm.standar_spmi_id),
          // Merge satuan into target if needed or skip. API expects target_value string.
          target_value: indikatorForm.target_value + (indikatorForm.satuan ? ` ${indikatorForm.satuan}` : '')
        };
        // Clean up separate satuan field for payload
        // Actually target_value logic: if editing, maybe we should just use target_value as is if user typed in it
        // Let's simplified: just use target_value from input.

        if (editItem) await adminService.indikator.update(editItem.id, {
          ...payload,
          target_value: indikatorForm.target_value // user entered full string
        });
        else await adminService.indikator.create({
          ...payload,
          target_value: indikatorForm.target_value // user entered full string
        });
      } else if (modalType === 'periode') {
        const payload = { ...periodeForm };
        if (editItem) await adminService.periode.update(editItem.id, payload);
        else await adminService.periode.create(payload);
      }

      toast.success('Data berhasil disimpan');
      setShowModal(false);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan data');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: TabId, id: number) => {
    if (window.confirm('Yakin ingin menghapus data ini?')) {
      try {
        if (type === 'kategori') await adminService.kategori.delete(id);
        if (type === 'standar') await adminService.standar.delete(id);
        if (type === 'indikator') await adminService.indikator.delete(id);
        if (type === 'periode') await adminService.periode.delete(id);

        toast.success('Data berhasil dihapus');
        fetchData();
      } catch (e) {
        toast.error('Gagal menghapus data');
      }
    }
  };

  const handleSetActivePeriode = async (id: number) => {
    try {
      await adminService.periode.setActive(id);
      toast.success('Periode aktif diperbarui');
      fetchData();
    } catch (e) {
      toast.error('Gagal mengubah periode aktif');
    }
  }

  const tabs: { id: TabId; label: string; icon: React.ReactNode; count: number }[] = [
    { id: 'kategori', label: 'Kategori Masalah', icon: <Tag className="w-4 h-4" />, count: kategori.length },
    { id: 'standar', label: 'Standar SPMI', icon: <BookOpen className="w-4 h-4" />, count: standar.length },
    { id: 'indikator', label: 'Indikator SPMI', icon: <Target className="w-4 h-4" />, count: indikator.length },
    { id: 'periode', label: 'Periode', icon: <Calendar className="w-4 h-4" />, count: periode.length },
  ];

  const getModalTitle = () => {
    const action = editItem ? 'Edit' : 'Tambah';
    const labels: Record<TabId, string> = {
      kategori: 'Kategori Masalah',
      standar: 'Standar SPMI',
      indikator: 'Indikator SPMI',
      periode: 'Periode',
    };
    return `${action} ${labels[modalType]}`;
  };

  if (loading && !kategori.length && !standar.length) { // Initial load
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-main" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Master Data' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Master Data</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Kelola data referensi sistem</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-neutral-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSearch(''); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id
              ? 'border-teal-main text-teal-main'
              : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
              }`}
          >
            {tab.icon}
            {tab.label}
            <span className="bg-neutral-bg text-neutral-text-secondary text-xs font-bold px-1.5 py-0.5 rounded-full">
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <Card padding="sm">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light"
            />
          </div>
          <Button variant="primary" size="sm" onClick={() => openCreate(activeTab)} icon={<Plus className="w-4 h-4" />}>
            Tambah
          </Button>
        </div>
      </Card>

      {/* ============ TAB: KATEGORI ============ */}
      {activeTab === 'kategori' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kategori.filter(k => !search || k.nama.toLowerCase().includes(search.toLowerCase())).map((kat) => (
            <Card key={kat.id} hover className="relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: kat.color }} />
              <div className="flex items-start justify-between pt-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: kat.color + '15' }}
                  >
                    <div style={{ color: kat.color }}>
                      {getIconComponent(kat.icon)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-neutral-text-primary">{kat.nama}</h3>
                    <p className="text-xs text-neutral-text-secondary mt-0.5 line-clamp-2">{kat.deskripsi}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-border">
                <div className="flex items-center gap-2">
                  {/* We might not have jumlah_laporan unless backend sends it. Using placeholder or check type */}
                  <span className="text-xs text-neutral-text-secondary">{(kat as any).laporan_count || 0} laporan</span>
                  <div className="w-4 h-4 rounded border" style={{ backgroundColor: kat.color }} />
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit('kategori', kat)} className="p-1.5 rounded-lg hover:bg-neutral-bg">
                    <Edit className="w-4 h-4 text-neutral-text-secondary" />
                  </button>
                  <button onClick={() => handleDelete('kategori', kat.id)} className="p-1.5 rounded-lg hover:bg-danger/10">
                    <Trash2 className="w-4 h-4 text-danger" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
          {!loading && kategori.length === 0 && <p className="text-center col-span-full py-8 text-neutral-text-secondary">Belum ada kategori.</p>}
        </div>
      )}

      {/* ============ TAB: STANDAR SPMI ============ */}
      {activeTab === 'standar' && (
        <div className="space-y-3">
          {standar.filter(s => !search || s.nama.toLowerCase().includes(search.toLowerCase())).map((std) => (
            <Card key={std.id} hover>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-teal-main/10 rounded-xl flex items-center justify-center shrink-0">
                  <span className="text-lg font-black text-teal-main">{std.kode.split('-')[1] || std.kode.substring(0, 2)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">{std.kode}</span>
                  </div>
                  <h3 className="text-sm font-bold text-neutral-text-primary">{std.nama}</h3>
                  <p className="text-xs text-neutral-text-secondary mt-0.5 line-clamp-1">{std.deskripsi}</p>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-center">
                    <p className="text-lg font-bold text-teal-main">{(std as any).indikator_count || 0}</p>
                    <p className="text-[10px] text-neutral-text-secondary">Indikator</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit('standar', std)} className="p-1.5 rounded-lg hover:bg-neutral-bg">
                      <Edit className="w-4 h-4 text-neutral-text-secondary" />
                    </button>
                    <button onClick={() => handleDelete('standar', std.id)} className="p-1.5 rounded-lg hover:bg-danger/10">
                      <Trash2 className="w-4 h-4 text-danger" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
          {!loading && standar.length === 0 && <p className="text-center py-8 text-neutral-text-secondary">Belum ada standar SPMI.</p>}
        </div>
      )}

      {/* ============ TAB: INDIKATOR SPMI ============ */}
      {activeTab === 'indikator' && (
        <Card padding="sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-bg">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Kode</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Indikator</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Standar</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Target</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {indikator
                  .filter(i => !search || i.nama.toLowerCase().includes(search.toLowerCase()) || i.kode.toLowerCase().includes(search.toLowerCase()))
                  .map((ind) => (
                    <tr key={ind.id} className="border-b border-neutral-border hover:bg-neutral-bg/50">
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">{ind.kode}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-neutral-text-primary max-w-xs">
                        {ind.nama}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-neutral-text-secondary">{ind.standar?.kode} - {ind.standar?.nama}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-teal-main">{ind.target_value || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit('indikator', ind)} className="p-1.5 rounded-lg hover:bg-neutral-bg">
                            <Edit className="w-4 h-4 text-neutral-text-secondary" />
                          </button>
                          <button onClick={() => handleDelete('indikator', ind.id)} className="p-1.5 rounded-lg hover:bg-danger/10">
                            <Trash2 className="w-4 h-4 text-danger" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {!loading && indikator.length === 0 && <p className="text-center py-8 text-neutral-text-secondary">Belum ada indikator SPMI.</p>}
          </div>
        </Card>
      )}

      {/* ============ TAB: PERIODE ============ */}
      {activeTab === 'periode' && (
        <div className="space-y-3">
          {periode.filter(p => !search || p.nama.toLowerCase().includes(search.toLowerCase())).map((per) => (
            <Card key={per.id} hover className={per.is_active ? 'border-l-4 border-l-success' : ''}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${per.is_active ? 'bg-success/10' : 'bg-neutral-bg'}`}>
                    <Calendar className={`w-6 h-6 ${per.is_active ? 'text-success' : 'text-neutral-text-secondary'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-neutral-text-primary">{per.nama}</h3>
                      {per.is_active && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-success/10 text-success">
                          AKTIF
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      {new Date(per.tanggal_mulai).toLocaleDateString('id-ID')} — {new Date(per.tanggal_selesai).toLocaleDateString('id-ID')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {/* <div className="text-center">
                    <p className="text-lg font-bold text-neutral-text-primary">{per.jumlah_laporan}</p>
                    <p className="text-[10px] text-neutral-text-secondary">Laporan</p>
                  </div> */}
                  <div className="flex items-center gap-1">
                    {!per.is_active && (
                      <button onClick={() => handleSetActivePeriode(per.id)} className="p-1.5 rounded-lg hover:bg-success/10 text-success" title="Set Aktif">
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => openEdit('periode', per)} className="p-1.5 rounded-lg hover:bg-neutral-bg">
                      <Edit className="w-4 h-4 text-neutral-text-secondary" />
                    </button>
                    {!per.is_active && (
                      <button onClick={() => handleDelete('periode', per.id)} className="p-1.5 rounded-lg hover:bg-danger/10">
                        <Trash2 className="w-4 h-4 text-danger" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
          {!loading && periode.length === 0 && <p className="text-center py-8 text-neutral-text-secondary">Belum ada periode.</p>}
        </div>
      )}

      {/* ============ MODAL ============ */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={getModalTitle()}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>Simpan</Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Kategori Form */}
          {modalType === 'kategori' && (
            <>
              <Input label="Nama Kategori" value={kategoriForm.nama} onChange={(e) => setKategoriForm({ ...kategoriForm, nama: e.target.value })} required />
              <Textarea label="Deskripsi" value={kategoriForm.deskripsi} onChange={(e) => setKategoriForm({ ...kategoriForm, deskripsi: e.target.value })} rows={3} />
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                  Warna <span className="text-danger">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={kategoriForm.color}
                    onChange={(e) => setKategoriForm({ ...kategoriForm, color: e.target.value })}
                    className="w-12 h-10 rounded border border-neutral-border cursor-pointer"
                  />
                  <Input
                    value={kategoriForm.color}
                    onChange={(e) => setKategoriForm({ ...kategoriForm, color: e.target.value })}
                    placeholder="#002060"
                    className="flex-1"
                  />
                </div>
              </div>
              <Select
                label="Icon"
                options={[
                  { value: '', label: 'Pilih Icon' },
                  { value: 'BookOpen', label: '📖 Akademik' },
                  { value: 'Users', label: '👥 Users' },
                  { value: 'Microscope', label: '🔬 Riset' },
                  { value: 'Heart', label: '❤️ Kemahasiswaan' },
                  { value: 'Shield', label: '🛡️ Infrastruktur' },
                  { value: 'Tag', label: '🏷️ Tag' },
                ]}
                value={kategoriForm.icon}
                onChange={(val) => setKategoriForm({ ...kategoriForm, icon: val })}
              />
            </>
          )}

          {/* Standar Form */}
          {modalType === 'standar' && (
            <>
              <Input label="Kode Standar" placeholder="STD-01" value={standarForm.kode} onChange={(e) => setStandarForm({ ...standarForm, kode: e.target.value })} required />
              <Input label="Nama Standar" value={standarForm.nama} onChange={(e) => setStandarForm({ ...standarForm, nama: e.target.value })} required />
              <Textarea label="Deskripsi" value={standarForm.deskripsi} onChange={(e) => setStandarForm({ ...standarForm, deskripsi: e.target.value })} rows={3} />
            </>
          )}

          {/* Indikator Form */}
          {modalType === 'indikator' && (
            <>
              <Select
                label="Standar SPMI"
                options={standar.map(s => ({ value: s.id.toString(), label: `${s.kode} - ${s.nama}` }))}
                placeholder="Pilih standar"
                value={indikatorForm.standar_spmi_id}
                onChange={(val) => setIndikatorForm({ ...indikatorForm, standar_spmi_id: val })}
                required
              />
              <Input label="Kode Indikator" placeholder="IND-5.1" value={indikatorForm.kode} onChange={(e) => setIndikatorForm({ ...indikatorForm, kode: e.target.value })} required />
              <Input label="Nama Indikator" value={indikatorForm.nama} onChange={(e) => setIndikatorForm({ ...indikatorForm, nama: e.target.value })} required />
              <Textarea label="Deskripsi" value={indikatorForm.deskripsi} onChange={(e) => setIndikatorForm({ ...indikatorForm, deskripsi: e.target.value })} rows={2} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Target Value" placeholder=">= 80%" value={indikatorForm.target_value} onChange={(e) => setIndikatorForm({ ...indikatorForm, target_value: e.target.value })} required />
                <Input label="Satuan" placeholder="Persen (Optional)" value={indikatorForm.satuan} onChange={(e) => setIndikatorForm({ ...indikatorForm, satuan: e.target.value })} />
              </div>
            </>
          )}

          {/* Periode Form */}
          {modalType === 'periode' && (
            <>
              <Input label="Nama Periode" placeholder="Semester Ganjil 2025/2026" value={periodeForm.nama} onChange={(e) => setPeriodeForm({ ...periodeForm, nama: e.target.value })} required />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Tahun Ajaran" placeholder="2025/2026" value={periodeForm.tahun_ajaran} onChange={(e) => setPeriodeForm({ ...periodeForm, tahun_ajaran: e.target.value })} required />
                <Select
                  label="Semester"
                  options={[{ value: 'Ganjil', label: 'Ganjil' }, { value: 'Genap', label: 'Genap' }]}
                  value={periodeForm.semester}
                  onChange={(val) => setPeriodeForm({ ...periodeForm, semester: val })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Tanggal Mulai" type="date" value={periodeForm.tanggal_mulai} onChange={(e) => setPeriodeForm({ ...periodeForm, tanggal_mulai: e.target.value })} required />
                <Input label="Tanggal Selesai" type="date" value={periodeForm.tanggal_selesai} onChange={(e) => setPeriodeForm({ ...periodeForm, tanggal_selesai: e.target.value })} required />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MasterData;
